import React from "react";
import { Eye, MessageSquare, CheckCircle2, XCircle } from "lucide-react";
import { AccountAvatar, StatusPill, MediaThumb, fmtWhen, formatOf } from "@/lib/social";

export function PostTable({ posts, accounts, ws, onOpen, client = false, onQuick, readOnly = false }) {
  const acc = Object.fromEntries((accounts || []).map((a) => [a.id, a]));
  const cols = client ? ["Post", "Accounts", "Status", "Feedback", ...(readOnly ? [] : ["Actions"])] : ["Post", "Accounts", "Status", "Feedback", "Created by", "Last updated"];
  return (
    <>
    <MobileCards posts={posts} acc={acc} ws={ws} onOpen={onOpen} client={client && !readOnly} onQuick={onQuick} />
    <div className="hidden md:block rounded-2xl border border-[#EEEFEF] bg-white overflow-x-auto" data-testid="social-post-table">
      <table className="w-full text-sm min-w-[760px]">
        <thead><tr className="border-b border-[#EEEFEF] text-left">{cols.map((c) => <th key={c} className={`px-4 py-3 font-medium text-[#595D62] text-xs ${c === "Actions" ? "text-right" : ""}`}>{c}</th>)}</tr></thead>
        <tbody>
          {posts.map((p) => {
            const list = (p.account_ids || []).map((id) => acc[id]).filter(Boolean);
            const fb = (p.comments || []).length;
            return (
              <tr key={p.id} data-testid={`social-post-row-${p.id}`} onClick={() => onOpen(p)} className="border-b border-[#f3f3f6] last:border-0 hover:bg-[#FAFAFD] cursor-pointer transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 max-w-[340px]">
                    <span className="relative h-[72px] w-[96px] shrink-0 rounded-lg overflow-hidden">
                      <MediaThumb m={p.media?.[0]} className="h-full w-full" />
                      <span className="absolute bottom-1 right-1 rounded bg-black/60 text-white text-[10px] px-1.5 py-0.5">{formatOf(p)}{p.media?.length > 1 ? ` · ${p.media.length}` : ""}</span>
                    </span>
                    <span className="text-[13px] text-[#06060C] line-clamp-4 leading-snug">{p.caption || <span className="text-[#9FA1A4] italic">No caption yet</span>}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center -space-x-2">
                    {list.slice(0, 2).map((a) => <AccountAvatar key={a.id} a={a} ws={ws} size={34} />)}
                    {list.length > 2 && <span className="h-[34px] w-[34px] rounded-full bg-[#F1F1F4] border-2 border-white text-[11px] text-[#595D62] flex items-center justify-center">+{list.length - 2}</span>}
                    {!list.length && <span className="text-xs text-[#c4c5cc]">None</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col items-start gap-1"><span className="flex items-center gap-1.5"><StatusPill status={p.status} testid={`social-post-status-${p.id}`} /><Eye className="h-3.5 w-3.5 text-[#9FA1A4]" /></span>
                    <span className="text-[11px] text-[#9FA1A4]">{p.scheduled_at ? fmtWhen(p.scheduled_at) : "No date"}</span></div>
                </td>
                <td className="px-4 py-3 text-[#595D62]">{fb ? <span className="inline-flex items-center gap-1 text-xs"><MessageSquare className="h-3.5 w-3.5" /> {fb}</span> : "—"}</td>
                {client ? (readOnly ? null :
                  <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {p.status === "with_client" ? (
                      <span className="inline-flex items-center gap-1.5">
                        <button data-testid={`social-quick-approve-${p.id}`} onClick={() => onQuick(p, "approve")} title="Approve" className="h-8 w-8 rounded-full flex items-center justify-center text-[#1f9d55] hover:bg-[#E6F7EE]"><CheckCircle2 className="h-5 w-5" /></button>
                        <button data-testid={`social-quick-reject-${p.id}`} onClick={() => onOpen(p, "reject")} title="Reject" className="h-8 w-8 rounded-full flex items-center justify-center text-rose-500 hover:bg-rose-50"><XCircle className="h-5 w-5" /></button>
                        <button data-testid={`social-quick-comment-${p.id}`} onClick={() => onOpen(p)} title="Comment" className="h-8 w-8 rounded-full flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD]"><MessageSquare className="h-4 w-4" /></button>
                      </span>
                    ) : <span className="text-xs text-[#9FA1A4]">{p.decided_by ? `by ${p.decided_by}` : ""}</span>}
                  </td>
                ) : (
                  <>
                    <td className="px-4 py-3 text-[#595D62] text-xs">{p.created_by}</td>
                    <td className="px-4 py-3 text-[#595D62] text-xs whitespace-nowrap">{fmtWhen(p.updated_at)}</td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}

function MobileCards({ posts, acc, ws, onOpen, client, onQuick }) {
  return (
    <div className="md:hidden space-y-3" data-testid="social-post-cards">
      {posts.map((p) => {
        const list = (p.account_ids || []).map((id) => acc[id]).filter(Boolean);
        const fb = (p.comments || []).length;
        return (
          <div key={p.id} data-testid={`social-post-card-${p.id}`} onClick={() => onOpen(p)} className="rounded-2xl border border-[#EEEFEF] bg-white overflow-hidden active:bg-[#FAFAFD] transition-colors">
            <div className="flex gap-3 p-3">
              <span className="relative h-24 w-24 shrink-0 rounded-xl overflow-hidden">
                <MediaThumb m={p.media?.[0]} className="h-full w-full" />
                <span className="absolute bottom-1 right-1 rounded bg-black/60 text-white text-[10px] px-1.5 py-0.5">{formatOf(p)}{p.media?.length > 1 ? ` · ${p.media.length}` : ""}</span>
              </span>
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="flex items-center gap-2"><StatusPill status={p.status} testid={`social-card-status-${p.id}`} />{fb > 0 && <span className="inline-flex items-center gap-1 text-xs text-[#595D62]"><MessageSquare className="h-3.5 w-3.5" /> {fb}</span>}</div>
                <p className="text-[13px] text-[#06060C] line-clamp-3 leading-snug">{p.caption || <span className="text-[#9FA1A4] italic">No caption yet</span>}</p>
                <div className="flex items-center gap-2"><span className="flex -space-x-2">{list.slice(0, 3).map((a) => <AccountAvatar key={a.id} a={a} ws={ws} size={24} />)}</span><span className="text-[11px] text-[#9FA1A4] truncate">{p.scheduled_at ? fmtWhen(p.scheduled_at) : "No date"}</span></div>
              </div>
            </div>
            {client && p.status === "with_client" && (
              <div className="grid grid-cols-3 border-t border-[#f3f3f6] text-sm" onClick={(e) => e.stopPropagation()}>
                <button data-testid={`social-card-approve-${p.id}`} onClick={() => onQuick(p, "approve")} className="py-3 flex items-center justify-center gap-1.5 text-[#1f9d55] active:bg-[#E6F7EE]"><CheckCircle2 className="h-4 w-4" /> Approve</button>
                <button data-testid={`social-card-reject-${p.id}`} onClick={() => onOpen(p, "reject")} className="py-3 flex items-center justify-center gap-1.5 text-rose-500 border-x border-[#f3f3f6] active:bg-rose-50"><XCircle className="h-4 w-4" /> Reject</button>
                <button data-testid={`social-card-view-${p.id}`} onClick={() => onOpen(p)} className="py-3 flex items-center justify-center gap-1.5 text-[#595D62] active:bg-[#F6F6FD]"><Eye className="h-4 w-4" /> View</button>
              </div>
            )}
            {client && p.status !== "with_client" && p.decided_by && <p className="px-3 pb-2.5 -mt-1 text-[11px] text-[#9FA1A4]">by {p.decided_by}</p>}
          </div>
        );
      })}
    </div>
  );
}
